// src/components/posts/PostForm.js
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import '../../styles/post-form.css';

const PostForm = ({ onPostCreated }) => {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [media, setMedia] = useState(null);
  const [mediaPreview, setMediaPreview] = useState('');
  const [mediaType, setMediaType] = useState(''); // 'image' or 'video'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const handleTextChange = (e) => {
    setText(e.target.value);
  };
  
  const validateVideoFile = (file) => {
    // Check file type
    if (!file.type.startsWith('video/')) {
      return 'Please select a video file';
    }
    
    // Check if it's MP4
    if (file.type !== 'video/mp4') {
      return 'Only MP4 video files are supported';
    }
    
    // Check file size (300MB limit)
    if (file.size > 298 * 1024 * 1024) {
      return 'Video must be less than 300MB';
    }
    
    return null;
  };
  
  const validateImageFile = (file) => {
    // Check file type
    if (!file.type.startsWith('image/')) {
      return 'Please select an image file';
    }
    
    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      return 'Image must be less than 5MB';
    }
    
    return null;
  };
  
  const getVideoDuration = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      
      video.src = URL.createObjectURL(file);
    });
  };
  
  const getVideoAspectRatio = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const aspectRatio = video.videoWidth / video.videoHeight;
        resolve({
          width: video.videoWidth,
          height: video.videoHeight,
          aspectRatio,
          isVertical: aspectRatio < 1
        });
      };
      
      video.src = URL.createObjectURL(file);
    });
  };
  
  const handleMediaChange = async (e) => {
    const file = e.target.files[0];
    
    if (!file) return;
    
    setError('');
    setUploadProgress(0);
    
    const fileType = file.type.startsWith('image/') ? 'image' : 'video';
    
    // Validate file based on type
    let validationError;
    if (fileType === 'image') {
      validationError = validateImageFile(file);
    } else if (fileType === 'video') {
      validationError = validateVideoFile(file);
      
      // Additional video validations
      if (!validationError) {
        try {
          const duration = await getVideoDuration(file);
          if (duration > 180) {
            validationError = 'Video duration cannot exceed 3 minutes (180 seconds)';
          }
        } catch (err) {
          validationError = 'Unable to process video file';
        }
      }
    } else {
      validationError = 'Please select an image or video file';
    }
    
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setMedia(file);
    setMediaType(fileType);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setMediaPreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    // For videos, also show aspect ratio info
    if (fileType === 'video') {
      try {
        const { width, height, isVertical } = await getVideoAspectRatio(file);
        console.log(`Video dimensions: ${width}x${height}, Vertical: ${isVertical}`);
        
        // You could show this info to user if needed
        if (isVertical) {
          setError(''); // Clear any previous errors
          // Optionally show a success message about reel format
        }
      } catch (err) {
        console.error('Error getting video dimensions:', err);
      }
    }
  };
  
  const removeMedia = () => {
    setMedia(null);
    setMediaPreview('');
    setMediaType('');
    setUploadProgress(0);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!text.trim() && !media) {
      setError('Please add some text, an image, or a video');
      return;
    }
    
    setLoading(true);
    setError('');
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('text', text);
      
      if (media) {
        if (mediaType === 'image') {
          formData.append('image', media);
        } else if (mediaType === 'video') {
          formData.append('video', media);
        }
      }
      
      const response = await api.post('/api/posts', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      // Reset form
      setText('');
      setMedia(null);
      setMediaPreview('');
      setMediaType('');
      setUploadProgress(0);
      
      if (onPostCreated) {
        onPostCreated(response.data.post);
      }
      
      // Show success message for video uploads
      if (mediaType === 'video') {
        // You could add a toast notification here
        console.log('Video uploaded successfully! Processing for reel feed...');
      }
      
    } catch (err) {
      console.error('Error creating post:', err);
      setError(err.response?.data?.error || 'Failed to create post. Please try again.');
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const getIMGURL = (url) => {
    if (!url) return '/default-avatar.png';
    
    if (url.startsWith('ipfs://')) {
      const cid = url.replace('ipfs://', '');
      return `https://ipfs.io/ipfs/${cid}`;
    }
    
    return url;
  };
  
  const isVideoFile = mediaType === 'video';
  const canPost = !loading && (text.trim() || media);
  
  return (
    <div className="post-form-container">
      <div className="post-form-header">
        <img 
          src={getIMGURL(user?.profilePic) || '/default-avatar.png'}
          alt={user?.username}
          className="user-avatar"
        />
        
        <form onSubmit={handleSubmit} className="post-form">
          <textarea
            placeholder="What's happening? Share a moment, photo, or reel!"
            value={text}
            onChange={handleTextChange}
            disabled={loading}
            rows={3}
          />
          
          {/* Media Preview */}
          {mediaPreview && (
            <div className="media-preview">
              {isVideoFile ? (
                <div className="video-preview">
                  <video 
                    src={mediaPreview} 
                    controls 
                    className="preview-video"
                    style={{ maxHeight: '300px', width: '100%', objectFit: 'contain' }}
                  />
                  <div className="video-info">
                    <span className="video-badge">
                      {mediaType === 'video' ? '🎬 Video' : '📸 Image'}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="image-preview">
                  <img src={mediaPreview} alt="Upload preview" />
                </div>
              )}
              
              <button 
                type="button" 
                className="remove-media" 
                onClick={removeMedia}
                disabled={loading}
                title="Remove media"
              >
                ✕
              </button>
            </div>
          )}
          
          {/* Upload Progress */}
          {loading && uploadProgress > 0 && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <span className="progress-text">
                {isVideoFile ? 'Uploading video' : 'Uploading image'}... {uploadProgress}%
              </span>
            </div>
          )}
          
          {error && <div className="post-error">{error}</div>}
          
          <div className="post-form-actions">
            <div className="post-form-tools">
              {/* Image Upload */}
              <label className="upload-media-label" title="Add photo">
                <i className="media-icon">📷</i>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleMediaChange}
                  disabled={loading}
                />
              </label>
              
              {/* Video Upload */}
              <label className="upload-media-label" title="Add video/reel">
                <i className="media-icon">🎬</i>
                <input
                  type="file"
                  accept="video/mp4"
                  onChange={handleMediaChange}
                  disabled={loading}
                />
              </label>
              
              <div className="media-hints">
                <small>
                  📸 Images: up to 5MB | 🎬 Videos: MP4 only, up to 300MB, max 3min
                </small>
              </div>
            </div>
            
            <button 
              type="submit" 
              className="post-button"
              disabled={!canPost}
            >
              {loading ? (
                isVideoFile ? 'Uploading Video...' : 'Posting...'
              ) : (
                'Post'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostForm;